import {
  all,
  call,
  put,
  takeLatest,
  delay,
  takeEvery,
  select
} from 'redux-saga/effects';
import {
  FETCH_COMMENTS_REQUEST,
  fetchCommentsSuccess,
  fetchCommentsFailure,
  reportCommentFailure,
  reportCommentSuccess,
  REPORT_COMMENT_REQUEST,
  LIKE_COMMENT_REQUEST,
  likeCommentSuccess,
  likeCommentFailure,
  unlikeCommentFailure,
  unlikeCommentSuccess,
  UNLIKE_COMMENT_REQUEST,
  LIKE_CHILD_COMMENT_REQUEST,
  likeChildCommentSuccess,
  likeChildCommentFailure,
  UNLIKE_CHILD_COMMENT_REQUEST,
  unlikeChildCommentFailure,
  unlikeChildCommentSuccess,
  POST_COMMENT_REQUEST,
  postCommentSuccess,
  postCommentFailure,
  POST_REPLY_REQUEST,
  postReplySuccess,
  DELETE_COMMENT_REQUEST,
  deleteCommentFailure,
  deleteCommentSuccess,
  DELETE_REPLY_REQUEST,
  deleteReplyFailure,
  deleteReplySuccess,
  editParentFailure,
  editParentSuccess,
  EDIT_PARENT_REQUEST,
  editChildSuccess,
  editChildFailure,
  EDIT_CHILD_REQUEST,
  postReplyFailure,
  fetchParentCommentsSuccess,
  fetchParentCommentsFailure,
  FETCH_PARENT_COMMENTS_REQUEST,
  FETCH_CHILD_COMMENTS_ITERATOR_REQUEST,
  cancelChildCommentsRequest,
  fetchChildCommentsSuccess,
  fetchChildCommentsFailure,
  FETCH_CHILD_COMMENTS_REQUEST,
  fetchChildCommentsIteratorRequest,
  reportReplySuccess,
  REPORT_REPLY_REQUEST,
  reportReplyFailure,
  loadMoreParentCommentsSuccess,
  loadMoreParentCommentsFailure,
  loadMoreChildrenCommentsSuccess,
  loadMoreChildrenCommentsFailure,
  LOAD_MORE_PARENT_COMMENTS_REQUEST,
  LOAD_MORE_CHILDREN_COMMENTS_REQUEST
} from '../../actions/comments/commentsActions';
import { get, post, deleteReq, putReq } from '../../../services/api';
import {
  commentsEndpoint,
  likeCommentEndpoint,
  complaintCommentEndpoint,
  postCommentEndpoint,
  deleteOrEditCommentEndpoint,
  parentCommentsEndpoint,
  childrenCommentsEndpoint
} from '../../../services/endpoint';
import { getParentsWithoutLoadedChildren } from '../../utils/utils';
import {
  TRANSLATE_PARENT_REQUEST,
  TRANSLATE_CHILD_REQUEST
} from '../../actions/comments/translation/translationActions';
import {
  translateParentSaga,
  translateChildSaga
} from './translation/translationSaga';
import {
  formatCommentFromApi,
  shortenMentioningTemplates
} from '../../../utils/mentioning/mentionFormatters';
import { getCommentsContext } from '../../selectors/settings/settingsSelectors';
import { COMMENTS_CONTEXT_SOCIAL_FEED } from '../../../utils/constants/commentConstants';

export function* fetchCommentsSaga(action) {
  const {
    payload: { contentId }
  } = action;
  try {
    const res = yield call(get, commentsEndpoint(contentId), {
      params: {
        all: true
      }
    });
    yield put(fetchCommentsSuccess(res));
  } catch (err) {
    const { response = {} } = err;
    yield put(fetchCommentsFailure(response));
  }
}

/**
 * Transforms a comment with mentioned users into a template react-mentions will understand
 * @param {Object} comment - comment to transform
 * @returns {Object} a transformed comment
 */
const transformComment = (comment) => {
  const { mentioned_users: mentionedUsers, body } = comment;
  if (mentionedUsers && mentionedUsers.length > 0) {
    const formattedText = formatCommentFromApi(body, mentionedUsers);
    return {
      ...comment,
      body: formattedText
    };
  }
  return comment;
};

/**
 * Same as @transformComment except it does a list of comments
 * @param {Array<Object>} comments - list of comments to transform
 * @returns {Array<Object>} a list of transfromed comments
 */
const transformMultipleComments = (comments) =>
  comments.map((comment) => {
    return transformComment(comment);
  });

const getParentCommentRequestParams = (commentsContext) => {
  // Returns the latest parent comment with mentioning disabled (show_mentions omitted)
  if (commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED) {
    return {
      page: 1,
      per_page: 1
    };
  }

  return {
    show_mentions: true
  };
};

export function* fetchParentCommentsSaga(action) {
  const {
    payload: { contentId, communityIds = [] }
  } = action;
  try {
    const commentsContext = yield select(getCommentsContext);
    const params = {
      ...getParentCommentRequestParams(commentsContext),
      community_ids: communityIds
    };
    const res = yield call(get, parentCommentsEndpoint(contentId), { params });
    const { comments, last_page: isLastPage } = res;
    const payload = {
      comments: transformMultipleComments(comments),
      isLastPage
    };

    yield put(fetchParentCommentsSuccess(payload));

    // Disabled fetching children comments if we are on the social feed
    if (commentsContext !== COMMENTS_CONTEXT_SOCIAL_FEED) {
      const parentIds = getParentsWithoutLoadedChildren(comments);
      yield put(fetchChildCommentsIteratorRequest({ contentId, parentIds }));
    }
  } catch (err) {
    const { response = {} } = err;
    yield put(fetchParentCommentsFailure(response));
  }
}

export function* loadMoreParentComments(action) {
  const {
    payload: { contentId, timestamp: loadMoreTimestamp, communityIds = [] }
  } = action;
  try {
    const params = {
      show_mentions: true,
      // Parent comments are ordered from new to old
      // so we use the timestamp of the newest comment
      // we have to get comments before (older) that timestamp
      before_datetime: loadMoreTimestamp,
      community_ids: communityIds
      // Api takes communityId(s) as an array
    };

    const { comments, last_page: isLastPage } = yield call(
      get,
      parentCommentsEndpoint(contentId),
      {
        params
      }
    );

    yield put(
      loadMoreParentCommentsSuccess({
        comments: transformMultipleComments(comments),
        isLastPage
      })
    );
    // Once parent comments have been fetched, start getting their children.
    const parentIds = getParentsWithoutLoadedChildren(comments);
    yield put(fetchChildCommentsIteratorRequest({ contentId, parentIds }));
  } catch (err) {
    const { response = {} } = err;
    yield put(loadMoreParentCommentsFailure(response));
  }
}

export function* loadMoreChildrenComments(action) {
  const {
    payload: { contentId, timestamp: loadMoreTimestamp, parentId }
  } = action;
  try {
    const { comments, last_page: isLastPage } = yield call(
      get,
      childrenCommentsEndpoint(contentId, parentId),
      {
        params: {
          show_mentions: true,
          // Children are ordered from old to new
          // so we use the timestamp of the oldest comment we have
          // to get comments after (newer) that timestamp
          after_datetime: loadMoreTimestamp
        }
      }
    );
    yield put(
      loadMoreChildrenCommentsSuccess({
        comments: transformMultipleComments(comments),
        parentId,
        isLastPage
      })
    );
  } catch (err) {
    const { response = {} } = err;
    yield put(loadMoreChildrenCommentsFailure(response, { parentId }));
  }
}

/**
 * This function allows us to get all the children comments given a parentId comment
 * @param {Object} action the event triggered
 */
export function* fetchChildCommentsSaga(action) {
  const {
    payload: { contentId, parentId }
  } = action;

  try {
    const res = yield call(
      get,
      childrenCommentsEndpoint(contentId, parentId, {
        params: {
          show_mentions: true
        }
      })
    );
    const { comments: childComments } = res;
    yield put(
      fetchChildCommentsSuccess({
        childComments: transformMultipleComments(childComments),
        parentId
      })
    );
  } catch (err) {
    const { response = {} } = err;
    yield put(fetchChildCommentsFailure(response, { parentId }));
  }
}

/**
 * This function is being used inside a loop that is iterating over each child
 * @param {Number} contentId id for a given content
 * @param {Number} parentId id for a given parent
 */
export const THROTTLE_API_REQUESTS = 300;

export function* fetchChildCommentsInIteratorSaga({ contentId, parentId }) {
  try {
    yield delay(THROTTLE_API_REQUESTS);
    const response = yield call(
      get,
      childrenCommentsEndpoint(contentId, parentId),
      {
        params: {
          show_mentions: true
        }
      }
    );

    const {
      comments: childComments,
      last_page: isLastPage,
      total_count: totalCount
    } = response;
    if (childComments.length > 0) {
      yield put(
        fetchChildCommentsSuccess({
          childComments: transformMultipleComments(childComments),
          parentId,
          isLastPage,
          totalCount
        })
      );
    } else {
      yield put(cancelChildCommentsRequest({ parentId }));
    }
  } catch (err) {
    const { response = {} } = err;
    yield put(fetchChildCommentsFailure(response, { parentId }));
  }
}

/**
 * This function is going to mapping over all the parents for a given content
 * @param {Object} action the event triggered
 */
export const childSagas = (action) => {
  const {
    payload: { contentId, parentIds = [] }
  } = action;

  return parentIds.map(function*(parentId) {
    return yield call(fetchChildCommentsInIteratorSaga, {
      contentId,
      parentId
    });
  });
};

/**
 * This function allows us to call all the children sagas
 * @param {Object} action the event triggered
 */
export function* fetchChildCommentsIteratorSaga(action) {
  yield all(childSagas(action));
}

export function* reportCommentSaga(action) {
  const {
    payload: { contentId, commentId }
  } = action;

  try {
    const response = yield call(
      post,
      complaintCommentEndpoint(contentId, commentId)
    );
    if (response.id) {
      yield put(reportCommentSuccess(commentId));
    } else {
      yield put(reportCommentFailure(response));
    }
  } catch (err) {
    const { response = {} } = err;
    yield put(reportCommentFailure(response));
  }
}

export function* reportReplySaga(action) {
  const {
    payload: { contentId, commentId, rootId }
  } = action;

  try {
    const response = yield call(
      post,
      complaintCommentEndpoint(contentId, commentId)
    );
    if (response.id) {
      yield put(reportReplySuccess({ commentId, rootId }));
    } else {
      yield put(reportReplyFailure(response));
    }
  } catch (err) {
    const { response = {} } = err;
    yield put(reportReplyFailure(response));
  }
}

export function* likeCommentSaga(action) {
  const {
    payload: { contentId, commentId }
  } = action;

  try {
    const response = yield call(
      post,
      likeCommentEndpoint(contentId, commentId)
    );
    if (response.success) {
      yield put(likeCommentSuccess(commentId));
    } else {
      yield put(likeCommentFailure(response, commentId));
    }
  } catch (err) {
    const { response = {} } = err;
    yield put(likeCommentFailure(response, commentId));
  }
}

export function* unlikeCommentSaga(action) {
  const {
    payload: { contentId, commentId }
  } = action;
  try {
    yield call(deleteReq, likeCommentEndpoint(contentId, commentId));
    yield put(unlikeCommentSuccess());
  } catch (err) {
    const { response = {} } = err;
    yield put(unlikeCommentFailure(response, commentId));
  }
}

export function* likeChildCommentSaga(action) {
  const {
    payload: { parentId, contentId, commentId }
  } = action;
  try {
    const response = yield call(
      post,
      likeCommentEndpoint(contentId, commentId)
    );
    if (response.success) {
      yield put(likeChildCommentSuccess({ parentId, commentId }));
    } else {
      yield put(likeChildCommentFailure(response, commentId, parentId));
    }
  } catch (err) {
    const { response = {} } = err;
    yield put(likeChildCommentFailure(response, commentId, parentId));
  }
}

export function* unlikeChildCommentSaga(action) {
  const {
    payload: { parentId, contentId, commentId }
  } = action;
  try {
    yield call(deleteReq, likeCommentEndpoint(contentId, commentId));
    yield put(unlikeChildCommentSuccess());
  } catch (err) {
    const { response = {} } = err;
    yield put(unlikeChildCommentFailure(response, parentId, commentId));
  }
}

export function* postCommentSaga(action) {
  const {
    payload: { contentId, commentText, communityId, parentId, rootId }
  } = action;
  const cleanedCommentText = shortenMentioningTemplates(commentText);
  const postData = {
    id: contentId,
    comment: cleanedCommentText,
    community_id: communityId,
    parent_id: parentId
  };
  try {
    const postResult = yield call(
      post,
      postCommentEndpoint(contentId),
      postData,
      {
        params: {
          show_mentions: true
        }
      }
    );
    if (postResult && postResult.comment && postResult.comment.id) {
      const commentsContext = yield select(getCommentsContext);
      if (
        postResult &&
        postResult.comment &&
        postResult.comment.ancestry &&
        postResult.comment.ancestry.parent_comment_id
      ) {
        yield put(
          postReplySuccess(
            transformComment(postResult.comment),
            commentsContext
          )
        );
      } else {
        yield put(
          postCommentSuccess(
            transformComment(postResult.comment),
            commentsContext
          )
        );
      }
    } else {
      yield rootId
        ? put(
            postReplyFailure(
              {
                response: { message: 'Post reply request failed.' }
              },
              rootId
            )
          )
        : put(
            postCommentFailure(
              {
                response: { message: 'Post comment request failed.' }
              },
              contentId
            )
          );
    }
  } catch (err) {
    // added extra check incase error is not thrown by API (standard JS error in try/catch block)
    const { response = {}, message = '' } = err;
    if (!response.message && message) {
      response.message = message;
    }

    yield rootId
      ? put(postReplyFailure(response, rootId))
      : put(postCommentFailure(response, contentId));
  }
}

export function* deleteCommentSaga(action) {
  const {
    payload: { contentId, commentId }
  } = action;
  try {
    const response = yield call(
      deleteReq,
      deleteOrEditCommentEndpoint(contentId, commentId)
    );
    if (response && response.id && response.id === commentId) {
      yield put(deleteCommentSuccess(response));
    } else {
      yield put(deleteCommentFailure({ commentId }));
    }
  } catch (err) {
    yield put(deleteCommentFailure({ commentId }));
  }
}

export function* deleteReplySaga(action) {
  const {
    payload: { contentId, commentId, rootId }
  } = action;
  try {
    const response = yield call(
      deleteReq,
      deleteOrEditCommentEndpoint(contentId, commentId)
    );
    if (response && response.id && response.id === commentId) {
      // Full reponse object is needed
      // for analytics properties. The rootId
      // is passed in from here as it isn't in
      // the response object
      yield put(deleteReplySuccess(response, rootId));
    } else {
      yield put(deleteReplyFailure({ commentId, rootId }));
    }
  } catch (err) {
    yield put(deleteReplyFailure({ commentId, rootId }));
  }
}

export function* editCommentSaga(action) {
  const {
    payload: { commentId, contentId, commentText }
  } = action;
  try {
    const response = yield call(
      putReq,
      deleteOrEditCommentEndpoint(contentId, commentId),
      {
        comment: shortenMentioningTemplates(commentText)
      },
      {
        params: {
          show_mentions: true
        }
      }
    );
    if (response && response.comment && response.comment.id === commentId) {
      yield put(editParentSuccess(transformComment(response.comment)));
    } else {
      yield put(editParentFailure(commentId));
    }
  } catch (err) {
    yield put(editParentFailure(commentId));
  }
}

export function* editReplySaga(action) {
  const {
    payload: { commentId, contentId, commentText, rootId }
  } = action;

  try {
    const response = yield call(
      putReq,
      deleteOrEditCommentEndpoint(contentId, commentId),
      {
        comment: shortenMentioningTemplates(commentText)
      },
      {
        params: {
          show_mentions: true
        }
      }
    );
    if (response && response.comment && response.comment.id === commentId) {
      yield put(editChildSuccess(transformComment(response.comment)));
    } else {
      yield put(editChildFailure({ commentId, rootId }));
    }
  } catch (err) {
    yield put(editChildFailure({ commentId, rootId }));
  }
}

export default function* root() {
  yield all([
    yield takeLatest(FETCH_COMMENTS_REQUEST, fetchCommentsSaga),
    yield takeLatest(FETCH_PARENT_COMMENTS_REQUEST, fetchParentCommentsSaga),
    yield takeLatest(
      FETCH_CHILD_COMMENTS_ITERATOR_REQUEST,
      fetchChildCommentsIteratorSaga
    ),
    yield takeLatest(FETCH_CHILD_COMMENTS_REQUEST, fetchChildCommentsSaga),
    yield takeLatest(REPORT_COMMENT_REQUEST, reportCommentSaga),
    yield takeLatest(REPORT_REPLY_REQUEST, reportReplySaga),
    yield takeLatest(LIKE_COMMENT_REQUEST, likeCommentSaga),
    yield takeLatest(UNLIKE_COMMENT_REQUEST, unlikeCommentSaga),
    yield takeLatest(LIKE_CHILD_COMMENT_REQUEST, likeChildCommentSaga),
    yield takeLatest(UNLIKE_CHILD_COMMENT_REQUEST, unlikeChildCommentSaga),
    yield takeLatest(POST_COMMENT_REQUEST, postCommentSaga),
    yield takeLatest(POST_REPLY_REQUEST, postCommentSaga),
    yield takeLatest(DELETE_COMMENT_REQUEST, deleteCommentSaga),
    yield takeLatest(DELETE_REPLY_REQUEST, deleteReplySaga),
    yield takeLatest(EDIT_PARENT_REQUEST, editCommentSaga),
    yield takeLatest(EDIT_CHILD_REQUEST, editReplySaga),
    yield takeLatest(TRANSLATE_PARENT_REQUEST, translateParentSaga),
    yield takeLatest(TRANSLATE_CHILD_REQUEST, translateChildSaga),
    yield takeLatest(LOAD_MORE_PARENT_COMMENTS_REQUEST, loadMoreParentComments),
    yield takeEvery(
      LOAD_MORE_CHILDREN_COMMENTS_REQUEST,
      loadMoreChildrenComments
    )
  ]);
}
